import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Box, CircularProgress, Typography } from "@mui/material";
import ApprovalDashboard from "./Foreign Travel/ApprovalDashboard";
import RequestDetail from "./Foreign Travel/RequestDetail";
import RequestDetail2 from "./Foreign Travel/RequestDetail2";
import ApproveModal from "./Foreign Travel/ApproveModal";
import RejectModal from "./Foreign Travel/RejectModal";
import SuccessScreen from "./Foreign Travel/SuccessScreen";
import ApprovalHistory from "./Foreign Travel/ApprovalHistory";
import AttachmentViewerModal from "./Foreign Travel/AttachmentViewerModal";
import { INITIAL_TRAVEL_REQUESTS } from "./Foreign Travel/mockData";
import {
  getForeignTravelDetails,
  updateForeignTravelRequestStatus,
} from "../../action/foreignTravelActions";

export const mapApiItemToTravelRequest = (item, index) => {
  if (!item) return null;

  // Determine status from API field HFD_STATUS or status
  let status = "Pending";
  const rawStatus = String(item.HFD_STATUS || item.status || "").trim().toUpperCase();
  if (rawStatus === "A" || rawStatus === "APPROVED") {
    status = "Approved";
  } else if (rawStatus === "R" || rawStatus === "REJECTED") {
    status = "Rejected";
  } else if (rawStatus === "P" || rawStatus === "PENDING") {
    status = "Pending";
  }

  const depsVal = item.DEPS || item.departureDate;
  const arrVal = item.ARRIVAL || item.arrivalDate || item.returnDate;

  // Calculate duration if valid dates
  let duration = item.duration || "N/A";
  if (depsVal && arrVal) {
    const depDate = new Date(depsVal);
    const arrDate = new Date(arrVal);
    if (!isNaN(depDate.getTime()) && !isNaN(arrDate.getTime())) {
      const diffTime = Math.abs(arrDate - depDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      duration = `${diffDays} Days`;
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formattedDeparture = formatDate(depsVal);
  const formattedArrival = formatDate(arrVal);
  const dateRange =
    formattedDeparture !== "-" && formattedArrival !== "-"
      ? `${formattedDeparture} - ${formattedArrival}`
      : item.dateRange || "N/A";

  const refId = item.REF_NO ? `TR-${item.REF_NO}` : item.id || `TR-2026-${String(index + 1).padStart(3, "0")}`;

  const serviceNo = item.SERVICE_NO || item.serviceNo || item.employeeNo || "";
  const baseURL = axios.defaults.baseURL ;
  const formattedBaseURL = baseURL.endsWith("/") ? baseURL : `${baseURL}/`;
  const avatarUrl = serviceNo
    ? `${formattedBaseURL}home/GetUserImg?serviceNo=${serviceNo}`
    : item.avatar ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(
        item.Name || item.name || "User"
      )}&background=0056C6&color=fff`;

  return {
    id: refId,
    refNo: item.REF_NO || item.refNo || "",
    employeeNo: serviceNo || "N/A",
    serviceNo: serviceNo || "N/A",
    name: item.Name || item.name || "Unknown Employee",
    role: item.role || (item.REG_NO ? `Reg: ${item.REG_NO}` : "Executive"),
    department: item.department || "Department",
    avatar: avatarUrl,
    country: item.V_CONME || item.country || "N/A",
    departureDate: formattedDeparture,
    arrivalDate: formattedArrival,
    returnDate: formattedArrival,
    duration: duration,
    dateRange: dateRange,
    status: status,
    purpose: item.PURPOSE || item.purpose || "No purpose provided",
    coverStatus: item.COVER_STUS || "",
    recommendation: item.RECOMANDATION || "",
    financialDetails: item.financialDetails || {
      allowanceUSD: "2,500",
      allowanceLKR: "750,000",
      airTicketLKR: "300,000",
      visaFeesLKR: "45,000",
      othersLKR: "25,000",
    },
    attachments: item.attachments || [
      { id: "att-1", name: "Passport.pdf", type: "pdf", size: "2.4 MB" },
      { id: "att-2", name: "Approval Letter.jpg", type: "image", size: "1.2 MB" },
    ],
    comments: item.comments || "",
    history: item.history || [
      {
        step: "Request Submitted",
        date: formattedDeparture !== "-" ? formattedDeparture : "Submitted",
        by: item.Name || item.name || "Employee",
        note: "Initial request submitted",
        status: "completed",
      },
      {
        step: "Manager Approval",
        date: "-",
        by: "Department Head",
        note: "Manager Endorsed",
        status: "completed",
      },
      {
        step: "Director Approval",
        date: status === "Pending" ? "Pending" : "-",
        by: "Director",
        note: status === "Pending" ? "Waiting for director approval" : status,
        status: status === "Approved" ? "completed" : status === "Rejected" ? "rejected" : "pending",
      },
      {
        step: "Completed",
        date: "-",
        by: "-",
        note: "Final status",
        status: status === "Approved" ? "completed" : "upcoming",
      },
    ],
    rawApiData: item,
  };
};

function ForeignTravelApp() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get Redux state
  const foreignTravelState = useSelector((state) => state.foreignTravel) || {};
  const { requests: rawRequests = [], loading = false, error = null } = foreignTravelState;

  // Local state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentView, setCurrentView] = useState("DASHBOARD");
  const [detailStyle, setDetailStyle] = useState("V1");

  // Modals state
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openAttachmentModal, setOpenAttachmentModal] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  // Comments and action type
  const [comment, setComment] = useState("");
  const [actionType, setActionType] = useState("Approved");

  // Fetch API data on mount
  useEffect(() => {
    dispatch(getForeignTravelDetails());
  }, [dispatch]);

  // Transform raw requests from API or fallback to mock data
  const requests = useMemo(() => {
    if (rawRequests && rawRequests.length > 0) {
      return rawRequests.map((item, index) => mapApiItemToTravelRequest(item, index));
    }
    // Fallback to mock data if API response is empty/not connected
    return INITIAL_TRAVEL_REQUESTS.map((item, index) => mapApiItemToTravelRequest(item, index));
  }, [rawRequests]);

  // Keep selectedRequest updated when requests list changes
  useEffect(() => {
    if (selectedRequest) {
      const updated = requests.find((r) => r.id === selectedRequest.id);
      if (updated) {
        setSelectedRequest(updated);
      }
    }
  }, [requests, selectedRequest]);

  // Handlers
  const handleSelectRequest = (req) => {
    setSelectedRequest(req);
    setComment(req.comments || "");
    setCurrentView("DETAIL");
  };

  const handleBackToApprovals = () => {
    navigate("/approvals");
  };

  const handleBackToDashboard = () => {
    setCurrentView("DASHBOARD");
  };

  const handleOpenApprove = () => {
    setOpenApproveModal(true);
  };

  const handleOpenReject = () => {
    setOpenRejectModal(true);
  };

  const handleConfirmApproval = () => {
    if (!selectedRequest) return;

    const updatedHistory = [
      ...selectedRequest.history.map((h) => {
        if (h.step === "Director Approval") {
          return {
            ...h,
            status: "completed",
            date:
              new Date().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }) +
              " " +
              new Date().toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              }),
            note: comment ? `Approved: ${comment}` : "Approved by Director",
          };
        }
        if (h.step === "Completed") {
          return {
            ...h,
            status: "completed",
            date: new Date().toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }),
            note: "Travel authorization finalized",
          };
        }
        return h;
      }),
    ];

    const updated = {
      ...selectedRequest,
      status: "Approved",
      comments: comment,
      history: updatedHistory,
      rawApiData: {
        ...selectedRequest.rawApiData,
        HFD_STATUS: "A",
      },
    };

    dispatch(updateForeignTravelRequestStatus(updated));
    setSelectedRequest(updated);
    setOpenApproveModal(false);
    setActionType("Approved");
    setCurrentView("SUCCESS");
  };

  const handleConfirmRejection = (reason) => {
    if (!selectedRequest) return;

    const updatedHistory = [
      ...selectedRequest.history.map((h) => {
        if (h.step === "Director Approval") {
          return {
            ...h,
            status: "rejected",
            date:
              new Date().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }) +
              " " +
              new Date().toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              }),
            note: reason ? `Rejected: ${reason}` : "Rejected by Director",
          };
        }
        return h;
      }),
    ];

    const updated = {
      ...selectedRequest,
      status: "Rejected",
      comments: reason,
      history: updatedHistory,
      rawApiData: {
        ...selectedRequest.rawApiData,
        HFD_STATUS: "R",
      },
    };

    dispatch(updateForeignTravelRequestStatus(updated));
    setSelectedRequest(updated);
    setOpenRejectModal(false);
    setActionType("Rejected");
    setCurrentView("SUCCESS");
  };

  const handleOpenAttachments = () => {
    setSelectedAttachment(null);
    setOpenAttachmentModal(true);
  };

  const handleSelectSpecificAttachment = (file) => {
    setSelectedAttachment(file);
    setOpenAttachmentModal(true);
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: "#F2F3F4" }}>
      {loading && requests.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            gap: 2,
          }}
        >
          <CircularProgress color="primary" />
          <Typography sx={{ color: "#64748B", fontWeight: 500 }}>
            Loading Foreign Travel Details...
          </Typography>
        </Box>
      ) : (
        <>
          {/* 1. Dashboard View */}
          {currentView === "DASHBOARD" && (
            <ApprovalDashboard
              requests={requests}
              onSelectRequest={handleSelectRequest}
              onBackToApprovals={handleBackToApprovals}
              notificationCount={3}
            />
          )}

          {/* 2. Request Details View - V2 */}
          {currentView === "DETAIL" && selectedRequest && detailStyle === "V2" && (
            <RequestDetail2
              request={selectedRequest}
              onBack={handleBackToDashboard}
              onOpenApprove={handleOpenApprove}
              onOpenReject={handleOpenReject}
              onOpenHistory={() => setCurrentView("HISTORY")}
              onOpenAttachments={handleOpenAttachments}
              onSwitchView={() => setDetailStyle("V1")}
            />
          )}

          {/* 2. Request Details View - V1 */}
          {currentView === "DETAIL" && selectedRequest && detailStyle === "V1" && (
            <RequestDetail
              request={selectedRequest}
              onBack={handleBackToDashboard}
              onOpenApprove={handleOpenApprove}
              onOpenReject={handleOpenReject}
              onOpenHistory={() => setCurrentView("HISTORY")}
              onOpenAttachments={handleOpenAttachments}
              onSelectAttachment={handleSelectSpecificAttachment}
              comment={comment}
              setComment={setComment}
              onSwitchView={() => setDetailStyle("V2")}
            />
          )}

          {/* 5. Success Screen */}
          {currentView === "SUCCESS" && (
            <SuccessScreen
              request={selectedRequest}
              actionType={actionType}
              onBackToDashboard={handleBackToDashboard}
            />
          )}

          {/* 6. Approval History */}
          {currentView === "HISTORY" && (
            <ApprovalHistory
              request={selectedRequest}
              onBack={() => setCurrentView("DETAIL")}
            />
          )}

          {/* 3. Approve Confirmation Modal */}
          <ApproveModal
            open={openApproveModal}
            onClose={() => setOpenApproveModal(false)}
            onConfirm={handleConfirmApproval}
            employeeName={selectedRequest?.name}
          />

          {/* 4. Reject Dialog Modal */}
          <RejectModal
            open={openRejectModal}
            onClose={() => setOpenRejectModal(false)}
            onConfirm={handleConfirmRejection}
            employeeName={selectedRequest?.name}
          />

          {/* Attachments Preview Modal */}
          <AttachmentViewerModal
            open={openAttachmentModal}
            onClose={() => setOpenAttachmentModal(false)}
            attachments={selectedRequest?.attachments || []}
            selectedAttachment={selectedAttachment}
          />
        </>
      )}
    </Box>
  );
}

export default ForeignTravelApp;
